import React, { useRef } from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Pressable,
  StyleSheet,
  Text,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../styles/theme';

interface PrimaryCTAProps {
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** MaterialIcons glyph to render to the right of the label. Default: 'arrow-forward'. Pass `null` for no icon. */
  iconRight?: keyof typeof MaterialIcons.glyphMap | null;
  /** FE-258.3 — forwarded to the inner Pressable so callers can target the
   *  rendered button from Playwright + RN testing-library. */
  testID?: string;
}

/**
 * Fidelity Purple primary button with press-scale animation + loading spinner
 * + optional trailing icon. Used as the main submit CTA on all auth screens.
 */
export const PrimaryCTA: React.FC<PrimaryCTAProps> = ({
  label,
  onPress,
  loading = false,
  disabled = false,
  iconRight = 'arrow-forward',
  testID,
}) => {
  const scale = useRef(new Animated.Value(1)).current;

  const pressIn = () =>
    Animated.timing(scale, {
      toValue: 0.97,
      duration: 90,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start();

  const pressOut = () =>
    Animated.timing(scale, {
      toValue: 1,
      duration: 140,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();

  const isDisabled = disabled || loading;

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Pressable
        onPress={onPress}
        onPressIn={pressIn}
        onPressOut={pressOut}
        disabled={isDisabled}
        style={[styles.cta, isDisabled && styles.ctaDisabled]}
        accessibilityRole="button"
        accessibilityLabel={label}
        // FE #154 — surface loading state to screen readers.
        // Without this, the in-flight Pressable shows only an
        // ActivityIndicator with no announced state.
        accessibilityState={{ disabled: isDisabled, busy: loading }}
        testID={testID}
      >
        {loading ? (
          <ActivityIndicator color={fpColors.onPrimary} />
        ) : (
          <>
            <Text style={styles.label}>{label}</Text>
            {iconRight && (
              <MaterialIcons
                name={iconRight}
                size={20}
                color={fpColors.onPrimary}
              />
            )}
          </>
        )}
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: fpColors.primary,
    paddingVertical: 16,
    borderRadius: 12,
    minHeight: 56,
    shadowColor: fpColors.primary,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 6,
  },
  ctaDisabled: {
    opacity: 0.6,
  },
  label: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onPrimary,
  },
});
